import type { TFunction } from "i18next";
import { ORGANIC_TAB_I18N_KEYS, ORGANIC_TAB_ITEMS } from "src/dashboards/organicMedia/components/Tabs/OrganicTabsDashboard.data";
import { PAID_MEDIA_TAB_I18N_KEYS, PAID_MEDIA_TAB_ITEMS } from "src/dashboards/paidMedia/components/Tabs/PaidMediaTabsDashboard.data";
import { SALES_PRODUCT_TAB_ITEMS } from "src/dashboards/salesProductAnalytics/components/SalesProductAnalyticsTabsDashboard.data";
import { OMNICHANNEL_TAB_ITEMS } from "src/dashboards/omnichannelAnalytics/components/OmnichannelAnalyticsTabsDashboard.data";
import { DASHBOARD_ROUTE_CONFIG } from "./DashboardRoutes";
import { HELP_SUPPORT_ROUTE, type SidebarSectionConfig } from "./SidebarConfig";

export interface SearchEntry {
  id: string;
  label: string;
  context?: string;
  route: string;
  tabId?: string;
}

interface DashboardTab {
  id: string;
  label: string;
}

export const MAX_SEARCH_RESULTS = 8;

const NO_MATCH = -1;

const DASHBOARDS_SECTION_KEY = "dashboards";

const DASHBOARD_TABS_BY_ROUTE: Record<string, (_t: TFunction) => DashboardTab[]> = {
  [DASHBOARD_ROUTE_CONFIG[304].route]: t =>
    SALES_PRODUCT_TAB_ITEMS.map(tab => ({
      id: tab.id,
      label: t(`salesProduct.tabs.${tab.id}`, { defaultValue: tab.label }),
    })),
  [DASHBOARD_ROUTE_CONFIG[302].route]: t =>
    PAID_MEDIA_TAB_ITEMS.map(tab => ({ id: tab.id, label: t(PAID_MEDIA_TAB_I18N_KEYS[tab.id]) })),
  [DASHBOARD_ROUTE_CONFIG[301].route]: t =>
    ORGANIC_TAB_ITEMS.map(tabId => ({ id: tabId, label: t(ORGANIC_TAB_I18N_KEYS[tabId]) })),
  [DASHBOARD_ROUTE_CONFIG[3].route]: () =>
    OMNICHANNEL_TAB_ITEMS.map(tab => ({ id: tab.id, label: tab.label })),
};

/** Flattens the (access-filtered) sidebar sections and dashboard tabs into searchable entries. */
export const buildSearchEntries = (
  t: TFunction,
  sections: SidebarSectionConfig[],
): SearchEntry[] => {
  const entries: SearchEntry[] = [];

  sections.forEach(section => {
    if (section.route) {
      entries.push({ id: section.route, label: section.label, route: section.route });
    }

    section.items?.forEach(({ name, route }) => {
      if (!route) {
        return;
      }

      entries.push({
        id: route,
        label: name,
        context: section.key === DASHBOARDS_SECTION_KEY ? undefined : section.label,
        route,
      });

      DASHBOARD_TABS_BY_ROUTE[route]?.(t).forEach(tab => {
        entries.push({
          id: `${route}#${tab.id}`,
          label: tab.label,
          context: name,
          route,
          tabId: tab.id,
        });
      });
    });
  });

  entries.push({
    id: HELP_SUPPORT_ROUTE,
    label: t("sidebar.sections.helpAndSupport"),
    route: HELP_SUPPORT_ROUTE,
  });

  return entries;
};

const getMatchRank = (entry: SearchEntry, query: string) => {
  const label = entry.label.toLocaleLowerCase();

  if (label.startsWith(query)) {
    return 0;
  }
  if (label.includes(query)) {
    return 1;
  }
  if (entry.context?.toLocaleLowerCase().includes(query)) {
    return 2;
  }
  return NO_MATCH;
};

/** Ranks label prefix matches first, then label matches, then parent-section matches. */
export const filterSearchEntries = (entries: SearchEntry[], query: string): SearchEntry[] => {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) {
    return [];
  }

  return entries
    .map(entry => ({ entry, rank: getMatchRank(entry, normalizedQuery) }))
    .filter(({ rank }) => rank !== NO_MATCH)
    .sort((first, second) => first.rank - second.rank)
    .slice(0, MAX_SEARCH_RESULTS)
    .map(({ entry }) => entry);
};
